import { Check, Copy, X as LinkedIn } from "lucide-react";
import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { cx } from "../util";
import { useFieldControl } from "./Field";

export interface TagInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Max number of chips. */
  max?: number;
  /** Lowercase every entry (good for keywords / hashtags). */
  lowercase?: boolean;
  /** Strip a leading character from each entry, e.g. "#" or "@". */
  stripPrefix?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  className?: string;
  "aria-label"?: string;
}

/**
 * Chips for keyword lists. Enter, comma or Tab commits; pasting a
 * comma/newline separated list adds them all; Backspace on an empty input
 * removes the last chip. Duplicates (case-insensitive) are ignored.
 */
export function TagInput({
  value,
  onChange,
  placeholder = "Type and press Enter",
  max,
  lowercase,
  stripPrefix,
  disabled,
  invalid,
  id,
  className,
  ...aria
}: TagInputProps) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const f = useFieldControl({ id, invalid });
  const full = max !== undefined && value.length >= max;

  function normalize(raw: string): string {
    let s = raw.trim();
    if (stripPrefix) while (s.startsWith(stripPrefix)) s = s.slice(stripPrefix.length);
    if (lowercase) s = s.toLowerCase();
    return s.trim();
  }

  function add(raws: string[]) {
    const seen = new Set(value.map((v) => v.toLowerCase()));
    const next = [...value];
    for (const r of raws) {
      const s = normalize(r);
      if (!s || seen.has(s.toLowerCase())) continue;
      if (max !== undefined && next.length >= max) break;
      seen.add(s.toLowerCase());
      next.push(s);
    }
    if (next.length !== value.length) onChange(next);
  }

  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
    inputRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if ((e.key === "Enter" || (e.key === "Tab" && draft.trim())) && !e.nativeEvent.isComposing) {
      if (!draft.trim()) {
        if (e.key === "Enter") e.preventDefault();
        return;
      }
      e.preventDefault();
      add([draft]);
      setDraft("");
    } else if (e.key === "Backspace" && !draft && value.length) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text");
    if (/[,\n]/.test(text)) {
      e.preventDefault();
      add(text.split(/[,\n]+/));
    }
  }

  return (
    <div
      className={cx("gf-tags", f.invalid && "gf-tags--invalid", disabled && "gf-tags--disabled", className)}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }}
    >
      <ul className="gf-tags__list" aria-label="Entries">
        {value.map((t, i) => (
          <li key={`${t}-${i}`} className="gf-tags__chip">
            <span className="gf-tags__text">{t}</span>
            {!disabled ? (
              <button type="button" className="gf-tags__remove" aria-label={`Remove ${t}`} onClick={() => remove(i)}>
                <LinkedIn size={12} strokeWidth={2.5} />
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      <input
        ref={inputRef}
        id={f.id}
        className="gf-tags__input"
        value={draft}
        disabled={disabled || full}
        placeholder={full ? `Max ${max}` : value.length ? "" : placeholder}
        aria-describedby={f["aria-describedby"]}
        aria-invalid={f["aria-invalid"]}
        aria-label={aria["aria-label"]}
        onChange={(e) => {
          // Commas commit, however they arrive (typing, IME, autofill, assistive tech).
          const v = e.target.value;
          if (v.includes(",")) {
            const parts = v.split(",");
            const rest = parts.pop() ?? "";
            add(parts);
            setDraft(rest);
          } else setDraft(v);
        }}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => {
          if (draft.trim()) {
            add([draft]);
            setDraft("");
          }
        }}
      />
    </div>
  );
}

export interface CopyFieldProps {
  value: string;
  /** Accessible name when not inside a <Field>. */
  "aria-label"?: string;
  className?: string;
  /** Called after a successful copy (e.g. to toast). */
  onCopy?: () => void;
  id?: string;
}

async function writeClipboard(text: string, fallbackEl: HTMLInputElement | null): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  if (!fallbackEl) return false;
  fallbackEl.focus();
  fallbackEl.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  }
}

/** Read-only value + copy button. Selects all on focus. */
export function CopyField({ value, className, onCopy, id, ...aria }: CopyFieldProps) {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const f = useFieldControl({ id });
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  return (
    <div className={cx("gf-copy", className)}>
      <input
        ref={inputRef}
        id={f.id}
        className="gf-copy__value"
        value={value}
        readOnly
        aria-describedby={f["aria-describedby"]}
        aria-label={aria["aria-label"]}
        onFocus={(e) => e.currentTarget.select()}
      />
      <button
        type="button"
        className={cx("gf-copy__btn", copied && "gf-copy__btn--done")}
        onClick={async () => {
          const ok = await writeClipboard(value, inputRef.current);
          if (ok) {
            setCopied(true);
            onCopy?.();
          }
        }}
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
        <span>{copied ? "Copied" : "Copy"}</span>
      </button>
      <span className="gf-sr-only" aria-live="polite">
        {copied ? "Copied to clipboard" : ""}
      </span>
    </div>
  );
}
